import type { Cents } from "./money";

/**
 * Core Project entity (Phase 1 scope: identity, status, basic property
 * information, and basic planning financials).
 *
 * IDs are UUID v4 strings generated at creation time. UUIDs were chosen over
 * autoincrement integers because the master specification anticipates a future
 * migration to a centralized/multi-device architecture (Section 10), where
 * globally unique IDs avoid merge collisions.
 *
 * Timestamps are ISO 8601 UTC strings.
 */
/** Project workflow family (spec §12A). Stable internal values. */
export type ProjectType = "renovation" | "new_construction";

export const PROJECT_TYPES: ReadonlyArray<{ id: ProjectType; displayName: string }> = [
  { id: "renovation", displayName: "Renovation" },
  { id: "new_construction", displayName: "New Construction" },
];

export interface Project {
  id: string;
  name: string;
  /**
   * Immutable human-readable Job Number (spec §12B), e.g. "26001":
   * YY + zero-padded annual sequence (expands past 999). Display identity
   * only — the UUID stays the sole internal identity / FK target.
   */
  jobNumber: string;
  /** renovation | new_construction — selected at creation (spec §12A). */
  projectType: ProjectType;
  /** References project_statuses.id (stable ID, never the display name). */
  statusId: string;
  streetAddress: string;
  city: string;
  state: string;
  zip: string;
  /** Integer cents; null = not entered. */
  purchasePriceCents: Cents | null;
  /** Expected After-Repair Value. Integer cents; null = not entered. */
  expectedArvCents: Cents | null;
  /**
   * Rehab contingency buffer, integer cents; null = not entered.
   * Kept SEPARATE from Proposed Rehab Cost (spec Section 24):
   * Total Authorized Rehab Budget = Proposed Rehab Cost + Contingency.
   * Edited via ProjectRepository.setContingency, not the project form.
   */
  contingencyCents: Cents | null;
  /**
   * Optional estimating sales-tax assumption for PROPOSED costs only,
   * hundredths of a percent (8.25% -> 825). Null = component prices are
   * treated as all-in. Actual Expenses always use their own real
   * pre-tax + tax fields; this never affects them.
   */
  estimatingTaxPctX100: number | null;
  /**
   * Sourcing market override (spec §26A): where this project shops. Null =
   * default to the project address ZIP. Edited via
   * ProjectRepository.setSourcingZip, not the project form.
   */
  sourcingZip: string | null;
  /** Provider store id this project prices against; null = resolve from ZIP. */
  sourcingStoreId: string | null;
  /** Spec §73: sell after renovating, or keep and rent. Never the project type. */
  strategy: ProjectStrategy;
  /** Heated living area in sq ft ×100 (spec §77); null = not known yet. */
  livingAreaSqftX100: number | null;
  notes: string;
  createdAt: string;
  updatedAt: string;
}

export type ProjectStrategy = "flip" | "rental";

/** The market a project sources from: explicit override, else address ZIP. */
export function effectiveSourcingZip(p: Pick<Project, "sourcingZip" | "zip">): string {
  return (p.sourcingZip ?? "").trim() !== "" ? p.sourcingZip!.trim() : p.zip.trim();
}

/** Fields the user supplies when creating or editing a project.
 *  `projectType` applies at creation (editable later, but the workflow is
 *  chosen up front); the Job Number is NEVER user-supplied. */
export interface ProjectInput {
  name: string;
  projectType?: ProjectType;
  statusId: string;
  streetAddress: string;
  city: string;
  state: string;
  zip: string;
  purchasePriceCents: Cents | null;
  expectedArvCents: Cents | null;
  notes: string;
  /**
   * Spec §86: asked when a project is first created. UNDEFINED means "leave
   * it as it is" on an edit — a form that does not show the field must never
   * silently reset a rental to a flip or erase a measured area.
   */
  strategy?: ProjectStrategy;
  livingAreaSqftX100?: number | null;
}

/** A project row as shown in the Projects list (includes resolved status name). */
export interface ProjectSummary extends Project {
  statusDisplayName: string;
}

/**
 * Basic Phase 1 validation. Returns a list of human-readable problems;
 * empty list means valid. Deliberately unrestrictive for fields whose
 * business rules are not yet defined by the specification.
 */
export function validateProjectInput(input: ProjectInput): string[] {
  const problems: string[] = [];
  if (input.name.trim() === "") {
    problems.push("Project name is required.");
  }
  if (input.statusId.trim() === "") {
    problems.push("Project status is required.");
  }
  for (const [label, value] of [
    ["Purchase Price", input.purchasePriceCents],
    ["Expected ARV", input.expectedArvCents],
  ] as const) {
    if (value !== null && !Number.isSafeInteger(value)) {
      problems.push(`${label} must be a valid amount.`);
    }
  }
  return problems;
}
